/**
 * Chat domain types
 * Centralized types for all chat-related functionality
 * This is the single source of truth for all chat types
 */

import type { Agent } from "../../../api/domains/agents/schemas/agent.schema";
import type {
  ChatByIdRecordDto,
  ChatWithMembersRecordDto
} from "../../../api/domains/chats/schemas/chat-record.schemas";
import type { DbEntity } from "../../../generated/prisma-models";
import type { WhatsappTemplateMessage } from "../../../schemas/Deployment";
import { BaseEntity, BaseTenantEntity, JsonValue, PaginatedResponse } from "../../core";
import type { AgentWithTasksAndTools } from "../agents/agent.types";
import type {
  ApiMetadata,
  EmailMetadata,
  EmbeddedMetadata,
  GoogleSheetMetadata,
  SlackSessionMetadata,
  TeamsSessionMetadata,
  TelegramMetadata,
  TwilioMetadata,
  WhatsappMetadata
} from "../deployment/deployment.types";
import type { PublicModelType } from "../tools/models.types";
import type { WorkflowStatusInfo } from "../tools/workflow.types";
import { MessageContent, type ToolCall, type ToolOutput } from "./message-content.types";

// ============================================================================
// CORE CHAT TYPES
// ============================================================================

/**
 * Chat Metadata - defines the structure of the metadata JSON field
 * Aligned with ChatMetadataSchema in validation/chat.schemas.ts
 *
 * This interface represents the actual metadata structure used in the backend
 * when creating chats from deployment sessions (see chat-messaging.service.ts)
 */
export interface ChatMetadata {
  /** Deployment session ID for tracking embedded chat sessions */
  sessionId?: string;
  /** Source of the chat (e.g., "web", "mobile", "api") */
  source?: string;

  /** Deployment-specific metadata fields */
  embeddedMetadata?: EmbeddedMetadata;
  slackMetadata?: SlackSessionMetadata;
  teamsMetadata?: TeamsSessionMetadata;
  twilioMetadata?: TwilioMetadata;
  telegramMetadata?: TelegramMetadata;
  emailMetadata?: EmailMetadata;
  googleSheetMetadata?: GoogleSheetMetadata;
  whatsappMetadata?: WhatsappMetadata;
  apiMetadata?: ApiMetadata;

  /** Additional custom fields */
  [key: string]: unknown;
}

/**
 * The chat row as the `Chat` table stores it.
 *
 * Every field is a column of `DbEntity.Chat`, with that column's own nullability:
 * a nullable column is `| null` and never `?:`, because Prisma returns `null` for
 * it and a full-row read always carries the key. The table has no `name`,
 * `createdById`, `description` or `currentAgentId` column, so none is declared here.
 * `organizationId` is nullable in the schema — legacy chats with no session hold
 * none — so it is not `BaseTenantEntity`'s non-null tenant id.
 *
 * `metadata` and `settings` are `Json?` columns: the domain shapes are offered
 * beside `JsonValue`, and a reader narrows with the schemas in `json-schemas`.
 */
export interface Chat extends BaseEntity {
  /** Organization owning the chat. Null only on legacy chats with no session. */
  organizationId: string | null;
  /** Current chat status */
  status: DbEntity.ChatStatus;
  /** Type of chat */
  type: DbEntity.ChatType;
  /** How responses are handled */
  responseHandling: DbEntity.ChatResponseHandling;
  /** Ticket status for support chats */
  ticketStatus: DbEntity.ChatTicketStatus;
  /** Additional metadata - structured as ChatMetadata */
  metadata: ChatMetadata | JsonValue | null;
  /** Chat settings and configuration */
  settings: ChatSettings | JsonValue | null;
  /** When chat was closed (if applicable) — a `Date` from Prisma, a string over JSON */
  closedAt: string | Date | null;
  /** Nano ID for external references */
  nanoId: string | null;
  /** Current meta conversation ID */
  currentMetaConversationId: string | null;
  /** Chat topic/subject */
  topic: string | null;
  /** Unread status */
  unread: boolean;
}

/**
 * A comment left on a chat by a user
 */
export interface ChatComment {
  id?: string;
  content: string;
  date: string;
  userId?: string;
}

/**
 * The session a chat read carries: what the members read selects of it, which
 * every other chat read selects too. The page and by-id reads add the thread id.
 */
export type ChatListDeploymentSession = NonNullable<ChatWithMembersRecordDto["deploymentSession"]> &
  Partial<Pick<NonNullable<ChatByIdRecordDto["deploymentSession"]>, "serviceThreadId">>;

/** The four columns every chat read selects of a chat's last message. */
export type ChatLastMessage = NonNullable<ChatWithMembersRecordDto["lastMessage"]>;

/** A member as every chat read carries one: the by-id read selects the user id alone. */
export type ChatListMember = ChatByIdRecordDto["members"][number];

/**
 * A chat as the inbox and playground lists hold it.
 *
 * Each relation is typed as the chat reads actually deliver it, so a component
 * reading a field no read selects fails to compile instead of reading `undefined`.
 * The reads come in through `toChatListItem` in the frontend, which turns their
 * nullable relations into absent ones and parses the JSON columns.
 */
export interface ChatWithMembers extends Chat {
  /** User members of the chat */
  members?: ChatListMember[];
  /** Deployment session data */
  deploymentSession?: ChatListDeploymentSession;
  /** Last message in the chat */
  lastMessage?: ChatLastMessage;
  /** Assigned users for support */
  assignedUsers?: { id: string }[];
  /** Number of pending messages */
  numberOfPendingMessages?: number;
  /** Snippet showing matched text from message search with <mark> highlighting */
  matchedMessageSnippet?: string;
  /** Comments on the chat */
  comments?: ChatComment[];
  agentId?: string | null;
  /** Whether this chat has an active code interpreter session (sandbox) */
  hasCodeInterpreterSession?: boolean;
  /** Agent directly assigned to this chat via agentId FK */
  agent?: Partial<Agent>;
}

// ============================================================================
// CHAT SETTINGS
// ============================================================================

/**
 * Comprehensive chat settings
 * Combines all settings from different systems
 */
/**
 * Simple WhatsApp template for chat settings (different from deployment WhatsApp templates)
 */
export interface ChatWhatsappTemplate {
  name: string;
  language: string;
  components?: unknown[];
}

export interface ChatSettings {
  // Core settings
  autoResponse?: boolean;
  responseDelay?: number;
  maxMessages?: number;
  allowFileUploads?: boolean;
  allowedFileTypes?: string[];

  // UI/UX settings
  theme?: ChatTheme;
  branding?: ChatBranding;

  // Feature toggles
  describeMode?: boolean;
  enableKnowledges?: boolean;
  enableInternetSearch?: boolean;
  enableSendDocuments?: boolean;
  enableDocumentsInChat?: boolean;
  enableDescribeMode?: boolean;
  showToolCalls?: boolean;
  showThinking?: boolean;
  disableSelectVirtualMember?: boolean;
  /** Declared by `ChatSettingsSchema`; read by `Chat.addContext`. */
  addContext?: boolean;

  // Commands and skills
  chatDefaultCommand?: string;
  enabledSkills?: { type: string; id: string }[];

  // Document handling
  sendDocumentsType?: AllowedFileFormats[];
  sendDocumentsConfigType?: "ALL" | "MAX_LENGTH" | "SKILLS";

  // Deployment type (determines which channel: EMAIL, SLACK, etc.)
  deploymentType?: DbEntity.DeploymentType;
  /** The channel the backend stamps on a chat it opens — a deployment type, or `"twilio"`. */
  integrationType?: string;
  nanoId?: string;
  embedData?: Record<string, string>;
  additionalTools?: {
    // Whatsapp
    whatsapp?: WhatsappTemplateMessage[];
  };

  // Suggested messages
  suggestedMessages?: string[];

  // Additional configuration
  promptAdditionalInformations?: string;
  metaConversationId?: string;
  draft?: boolean;
  hideHeader?: boolean;

  // Index signature for Prisma compatibility
  [key: string]: unknown;
}

/**
 * Chat theme configuration
 */
export interface ChatTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily?: string;
}

/**
 * Chat branding configuration
 */
export interface ChatBranding {
  logo?: string;
  title?: string;
  subtitle?: string;
  showPoweredBy: boolean;
}

// ============================================================================
// CHAT MEMBERS
// ============================================================================

/**
 * Chat member (user)
 */
export interface ChatMember extends BaseTenantEntity {
  chatId: string;
  userId: string;
  role: DbEntity.ChatMemberRole;
  joinedAt: string;
  lastActiveAt?: string;
  /** User relation */
  user?: {
    id: string;
    role: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    createdAt: Date;
    updatedAt: Date;
    tracking: unknown;
    onboardingDone: boolean;
  };
}

/**
 * Virtual member (AI agent)
 * Represents the relationship between a chat and an agent
 *
 * Matches Prisma ChatVirtualMember model exactly
 * Backend projection: { include: { agent: true } }
 */
export interface VirtualMember {
  agentId: string;
  chatId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  /** Full agent details from backend */
  agent: AgentWithTasksAndTools;
}

// ============================================================================
// MESSAGES
// ============================================================================

/**
 * Message metadata
 */
export interface MessageMetadata {
  model?: string;
  tokens?: number;
  processingTime?: number;
  confidence?: number;
  sources?: string[];
  [key: string]: unknown;
}

/**
 * Message attachment
 */
export interface MessageAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
}

/**
 * Message file
 * Aligned with Knowledge model fields from Prisma.
 * Files always have id and name - these come from database records.
 */
export interface MessageFile {
  id: string;
  name: string;
  textContent?: string | null;
}

/** Minimal message type for shared rendering components. CueFunctionCallMessage is excluded — renders as badges, not chat bubbles. */
export interface BaseChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  reasoning?: string;
  reasoningDurationMs?: number;
  createdAt?: string | Date;
}

// ============================================================================
// DEPLOYMENT & SESSION TYPES
// ============================================================================

/**
 * Deployment session
 */
export interface DeploymentSession {
  id: string;
  /** CRM customer record when the session is linked to a customer profile */
  customerId?: string | null;
  deployment: {
    id: string;
    name: string;
    type: DbEntity.DeploymentType;
  };
  /** Deployment ID (alias for deployment.id) */
  deploymentId?: string;
  /** Service thread ID (e.g., WhatsApp composite thread `wa:+X:+Y`) */
  serviceThreadId?: string;
  /** Customer identity linked to this session */
  customerIdentity?: {
    id: string;
    /** Clean customer identifier (phone number, email, etc.) */
    identifier: string;
    service: DbEntity.DeploymentType;
    /** Customer profile (displayName = WhatsApp profile name) */
    customer?: {
      displayName?: string | null;
      primaryEmail?: string | null;
      primaryPhone?: string | null;
    };
  };
  /** Call metadata for TWILIO_VOICE deployments (JSON field, present when callMetadata is selected). */
  callMetadata?: {
    recordingUrl?: string;
    isOutbound?: boolean;
    from?: string;
    to?: string;
    duration?: string | number;
    callSid?: string;
  } | null;
}

/**
 * Meta conversation data
 */
export interface MetaConversationData {
  id: string;
  status: DbEntity.MetaConversationStatus;
  createdAt: string;
  updatedAt: string;
  chatId: string;
  messages: DbEntity.Message[];
}

// ============================================================================
// FRONTEND-SPECIFIC TYPES
// ============================================================================

/**
 * Chat instance for Redux store
 */
export interface ChatInstance {
  /** Core identification */
  id: string;

  /** Message data */
  messages: SerializableChatMessage[];
  isLoadingMessages: boolean;

  /** Streaming state */
  streamingMessages: Record<string, StreamingMessage>;

  /** Input state */
  inputValue: string;
  attachedFiles: DbEntity.File[];

  /** Processing state */
  isSendingMessage: boolean;

  /** Dynamic whitespace layout: Show whitespace to position user message at top */
  shouldShowDynamicWhitespace: boolean;

  /** Chat metadata */
  topic?: string;
  metadata?: ChatMetadata | JsonValue;

  /** Context (optional) */
  context?: ChatContext;

  /** Deployment metadata (for embedded chats) */
  deploymentMetadata?: DeploymentMetadata;

  /** Timestamps */
  createdAt: number;
  lastAccessedAt: number;

  /** Unread message tracking */
  unreadCount: number;
  lastReadMessageId?: string;
}

/**
 * Code interpreter block types
 *
 * These are the canonical types for code interpreter blocks.
 * Used by both frontend (Redux, components) and backend (WebSocket payloads).
 */
export interface CodeInterpreterThinkingBlock {
  type: "thinking";
  id: string;
  content: string;
  isComplete?: boolean;
  durationMs?: number;
}

export interface CodeInterpreterToolCallBlock {
  type: "tool_call";
  id: string;
  toolId: string;
  name: string;
  input: unknown;
  output?: string;
  isError?: boolean;
  isComplete: boolean;
}

export interface CodeInterpreterTextBlock {
  type: "text";
  id: string;
  content: string;
}

export type CodeInterpreterBlock =
  | CodeInterpreterThinkingBlock
  | CodeInterpreterToolCallBlock
  | CodeInterpreterTextBlock;

/**
 * File generated by code interpreter in the sandbox and persisted to S3.
 *
 * Owner-conditional shape:
 * - CHAT files: uploaded with a private ACL. `publicUrl` is stored as the object's bare URL and
 *   every reader signs it for its audience; the bare value answers 403 on its own.
 * - CUE_CONVERSATION files: uploaded with a private ACL. `s3Key` is stored in the message JSONB;
 *   `url` is populated at read time by the resolver with a short-lived presigned inline URL.
 */
export interface CodeInterpreterFile {
  /** Display name of the file */
  name: string;
  /** DB record ID — used to generate gated download URLs */
  fileId: string;
  /** Original sandbox filesystem path */
  path?: string;
  /** Backend download URL: /api/chats/{chatId}/files/{fileId}/download */
  downloadUrl: string;
  /**
   * S3 object key — stored in CUE_CONVERSATION message JSONB for read-time presigning.
   * Absent on CHAT files, whose key is read off `publicUrl`.
   */
  s3Key?: string;
  /**
   * Short-lived presigned inline URL for CUE_CONVERSATION files — populated at read time by the
   * resolver, never stored. Use for <img src> and inline rendering; expires after ~1 hour.
   */
  url?: string;
  /**
   * CHAT files: the object's URL. Stored bare and signed by the server for each reader, so a
   * value a client receives is renderable and expires; the stored bare value is never served.
   * Also present on Cue messages persisted before the private-ACL migration (legacy rows),
   * where the read-time resolver re-signs via extractS3Key.
   */
  publicUrl?: string;
  /**
   * MIME type of the file (e.g. "image/png", "text/csv").
   * Derived from the filename at persist time via getMimeType.
   * Optional: absent on messages persisted before this field was added.
   */
  mediaType?: string;
}

/**
 * Streaming message state
 * Tracks both backend streaming (receiving data) and frontend display (showing animation)
 *
 * Supports two modes:
 * - Regular streaming: Uses `content` field for text accumulation
 * - Code interpreter: Uses `blocks` field for interleaved block display
 */
export interface StreamingMessage {
  /** Text content for regular streaming (non-code-interpreter) */
  content: string;
  /** Interleaved blocks for code interpreter streaming */
  blocks?: CodeInterpreterBlock[];
  /** Backend is still sending tokens via WebSocket */
  isStreaming: boolean;
  /** Backend signaled streaming is complete */
  isComplete: boolean;
  /** Frontend has finished displaying progressive animation */
  displayComplete: boolean;
  hasError: boolean;
  error?: {
    code: string;
    message: string;
  };
  /** Code interpreter: execution complete, waiting for final message with download links */
  isPreparingFiles?: boolean;
}

/**
 * Chat context types
 */
export type ChatContextType = "regular" | "inbox" | "embedded" | "preview" | "playground";

/**
 * Chat context metadata
 */
export interface ChatContext {
  type: ChatContextType;

  // For embedded/preview chats
  deploymentId?: string;
  sessionId?: string;
  agentId?: string;
  chatRoomId?: string;
  isPreviewMode?: boolean;

  // For inbox chats
  ticketStatus?: string;
  assignedTo?: string;
}

/**
 * Deployment metadata for embedded chats
 */
export interface DeploymentMetadata {
  name?: string;
  description?: string;
  agent?: Partial<Agent>;
  deploymentSettings?: {
    embedSettings?: {
      welcomeMessages?: string[];
      suggestedMessages?: string[];
      position?: string;
      displayName?: string;
      showHeader?: boolean;
      format?: string;
      textColor?: string;
      backgroundColor?: string;
    };
    advancedSettings?: {
      showToolsInput?: boolean;
      showToolsOutput?: boolean;
      showDownloadAndCopyButtons?: boolean;
      enabledRefreshConversation?: boolean;
      enableDocumentsInChat?: boolean;
    };
  };
}

/**
 * Serializable chat message for Redux
 * IMPORTANT: Content is ALWAYS a MessageContent object (never string!)
 * Backend normalizes all content to object format via normalizeMessageContent()
 */
export interface SerializableChatMessage {
  id: string;
  /** Message content - ALWAYS an object! {text, images?, tools?, ...} */
  content: MessageContent;
  contentType: DbEntity.MessageContentType;
  /** Message type (for frontend compatibility) */
  type: Extract<DbEntity.MessageContentType, "HUMAN" | "AI" | "SYSTEM" | "TOOL">;
  status: DbEntity.MessageStatus;
  createdAt: string;
  updatedAt: string;
  chatId: string;
  /** User who created the message */
  createdById: string;
  /** Message name/title */
  name?: string;
  /** Additional metadata */
  metadata?: MessageMetadata;
  /** Parent message ID for threading */
  parentId?: string;
  /** Thread ID for grouping */
  threadId?: string;
  /** Agent ID */
  agentId?: string;
  /** User ID who sent the message */
  userId?: string;
  /** Model ID used for generation */
  modelId?: string;
  /** Meta conversation ID */
  metaConversationId?: string;
  /** Tool outputs from AI - DEPRECATED: Parse content.tools instead */
  toolOutputs?: ToolOutput[];
  /** Message attachments */
  attachments?: MessageAttachment[];
  /**
   * Tool calls made by the AI turn. LOAD-BEARING — not deprecated.
   *
   * Written by `Message.endMessage`, persisted to the `toolCalls` column, and
   * selected back by the chat repository. It is the ONLY input to both surfaces
   * that render tool-call evidence — `renderToolCallBadges` and
   * `toContentParts` — so emptying it deletes every trace that a tool ran.
   *
   * `content.toolCalls` is not the replacement it looks like. It is declared,
   * on `ToolOutputContent` in `message-content.types.ts`, and it has no writer
   * and no reader, so a read routed through it returns nothing. The backend
   * also mirrors an ordered `content.parts`, which nothing on the chat read
   * path consumes yet.
   */
  toolCalls?: ToolCall[];
  /** Tool call ID for tracking */
  toolCallId?: string;
  /** Type of tool used */
  toolType?: DbEntity.ToolType;
  /** Message files - DEPRECATED: Parse content.files instead */
  files?: MessageFile[];
  /** Message images - DEPRECATED: Parse content.images instead */
  images?: string[];
  /** Organization ID */
  organizationId?: string;
  /** Timezone offset in hours */
  timezoneOffsetHours?: number;
  /** Text content (for compatibility) */
  textContent?: string;
  /** Deployment message ID */
  deploymentMessageId?: string;
  /** Run ID for tracking */
  runId?: string;
  /** Whether message was cancelled */
  cancelled?: boolean;
  /** Status information - primarily used for workflow execution tracking */
  statusInfos?: WorkflowStatusInfo | Record<string, unknown>;
  /** User object (from Prisma relation) */
  user?: DbEntity.User;
  /** Agent object (from Prisma relation) */
  agent?: DbEntity.Agent;
}

// ============================================================================
// REDUX STATE TYPES
// ============================================================================

/**
 * Chat global state
 */
export interface ChatGlobalState {
  availableChats: ChatWithMembers[];
  agents: Agent[];
  models: PublicModelType[];
  isLoading: boolean;
  error?: string;
}

/**
 * Chat active state
 */
export interface ChatActiveState {
  activeChatId: string | null;
  isCreating: boolean;
  isJoining: boolean;
  error?: string;
}

/**
 * Chat instance state
 */
export interface ChatInstanceState {
  instances: Record<string, ChatInstance>;
  isLoading: boolean;
  error?: string;
}

/**
 * Chat embedded state
 */
export interface ChatEmbeddedState {
  instances: Record<string, ChatInstance>;
  settings: Record<string, ChatSettings>;
  isLoading: boolean;
  error?: string;
}

/**
 * Chat inbox state
 */
export interface ChatInboxState {
  tickets: unknown[]; // Will be typed properly when ticket types are defined
  filters: InboxFilters;
  isLoading: boolean;
  error?: string;
}

/**
 * Inbox filters
 */
export interface InboxFilters {
  status?: DbEntity.ChatTicketStatus[];
  assignee?: string[];
  dateRange?: {
    from: string;
    to: string;
  };
  search?: string;
}

/**
 * Chat root state
 */
export interface ChatRootState {
  global: ChatGlobalState;
  active: ChatActiveState;
  instances: ChatInstanceState;
  embedded: ChatEmbeddedState;
  inbox: ChatInboxState;
}

// ============================================================================
// API TYPES
// ============================================================================

/**
 * Create chat request
 */
export interface CreateChatRequest {
  name: string;
  description?: string;
  type: DbEntity.ChatType;
  memberIds?: string[];
  settings?: Partial<ChatSettings>;
}

/**
 * Update chat request
 */
export interface UpdateChatRequest {
  name?: string;
  description?: string;
  status?: DbEntity.ChatStatus;
  settings?: Partial<ChatSettings>;
}

/**
 * Send message request
 */
export interface SendMessageRequest {
  content: string;
  contentType: DbEntity.MessageContentType;
  parentId?: string;
  threadId?: string;
  attachments?: unknown[]; // File[] - will be handled at runtime
  generateTopic?: boolean; // Request AI-generated topic for chat
}

/**
 * Chat list response
 */
export interface ChatListResponse extends PaginatedResponse<ChatWithMembers> {}

/**
 * Message list response
 */
export interface MessageListResponse extends PaginatedResponse<DbEntity.Message> {}

/**
 * Get chatroom of deployment response (embed chat handshake)
 * Used by frontend to initialize embed chat sessions
 *
 * @deprecated Use GetDeploymentChatroomResponse from @nexus/types instead
 * The canonical type is derived from ZDeployments.GetDeploymentChatroom
 */
export type { GetDeploymentChatroomResponse as GetChatroomOfDeploymentResponse } from "../../../api/domains/deployments/schemas/chatroom.schema";

// ============================================================================
// WEBSOCKET TYPES
// ============================================================================

/**
 * Chat WebSocket message
 */
export interface ChatWebSocketMessage {
  type: "message" | "typing" | "status" | "error";
  chatId: string;
  data: unknown;
  timestamp: string;
}

/**
 * Message WebSocket message
 */
export interface MessageWebSocketMessage extends ChatWebSocketMessage {
  type: "message";
  data: DbEntity.Message;
}

/**
 * Typing WebSocket message
 */
export interface TypingWebSocketMessage extends ChatWebSocketMessage {
  type: "typing";
  data: {
    userId: string;
    isTyping: boolean;
  };
}

/**
 * Status WebSocket message
 */
export interface StatusWebSocketMessage extends ChatWebSocketMessage {
  type: "status";
  data: {
    status: DbEntity.MessageStatus;
    messageId: string;
  };
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Allowed file formats - enum for runtime usage
 */
export enum AllowedFileFormats {
  PDF = "pdf",
  DOCX = "docx",
  TXT = "txt",
  JPG = "jpg",
  PNG = "png",
  GIF = "gif",
  MP4 = "mp4",
  MP3 = "mp3",
  WAV = "wav",
  ZIP = "zip",
  CSV = "csv",
  XLSX = "xlsx"
}

/**
 * Chat message plan
 */
export interface ChatMessagePlan {
  objective: string;
  steps: { value: string; name: string; description: string }[];
}

/**
 * Chat command
 */
export interface ChatCommand {
  name: string;
  action: string;
  description: string;
  example: string;
}

// ============================================================================
// CHAT LIST FILTER CONSTANTS
//
// API-level filter values for the chat list endpoint.
// These are NOT Prisma enums — they are shorthand values understood by the
// list endpoint and translated into Prisma queries on the server.
//
// Rule: never hardcode "inbox", "playground", "OPEN", etc. as string literals.
//       Always reference these constants.
//
// Usage:
//   if (type === ChatViewType.INBOX) { ... }       // ✅
//   if (type === "inbox") { ... }                   // ❌ hardcoded
//   z.nativeEnum(ChatViewType)                      // ✅ Zod integration
// ============================================================================

/**
 * Chat view type: which section the user is browsing.
 * - INBOX      → deployment sessions, assigned-user filtering applies
 * - PLAYGROUND → no deployment session, shows agent test chats
 */
export const ChatViewType = {
  INBOX: "inbox",
  PLAYGROUND: "playground"
} as const;
export type ChatViewType = (typeof ChatViewType)[keyof typeof ChatViewType];

/**
 * Chat list filter type.
 * - HISTORY  → active + non-archived chats
 * - ARCHIVED → only archived (closed) chats
 */
export const ChatFilterType = {
  HISTORY: "history",
  ARCHIVED: "archived"
} as const;
export type ChatFilterType = (typeof ChatFilterType)[keyof typeof ChatFilterType];

/**
 * API-level chat status filter.
 * - OPEN   → maps to Prisma { in: [ChatStatus.OPEN, ChatStatus.RUNNING] }
 * - CLOSED → maps to Prisma ChatStatus.ARCHIVED
 * Distinct from DbEnum.ChatStatus which has 4 raw Prisma values.
 */
export const ChatStatusFilter = {
  OPEN: "OPEN",
  CLOSED: "CLOSED"
} as const;
export type ChatStatusFilter = (typeof ChatStatusFilter)[keyof typeof ChatStatusFilter];

/**
 * Chat assignedTo filter.
 * - ME   → only chats assigned to the current user
 * - NONE → only chats with no assigned user
 */
export const ChatAssignedToFilter = {
  ME: "me",
  NONE: "none"
} as const;
export type ChatAssignedToFilter = (typeof ChatAssignedToFilter)[keyof typeof ChatAssignedToFilter];
